// src/components/SidebarProfile.jsx
import React from 'react';
import { useAuth } from '../context/AuthContext';
import ClientProfilePhoto from './ClientProfilePhoto';
import { StarIcon } from 'lucide-react';

/* ─── Inline styles for effects Tailwind can't express ─── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@600;700&family=Exo+2:wght@400;500&display=swap');

  .profile-card {
    position: relative;
    font-family: 'Exo 2', sans-serif;
    background: linear-gradient(135deg, #0d0120 0%, #1a0340 60%, #0d0120 100%);
    border-bottom: 2px solid #FF9200;
    overflow: hidden;
  }

  /* subtle scanline overlay */
  .profile-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 3px,
      rgba(255,146,0,0.03) 3px,
      rgba(255,146,0,0.03) 4px
    );
    pointer-events: none;
    z-index: 0;
  }

  /* corner accent */
  .profile-card::after {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 60px; height: 60px;
    background: linear-gradient(225deg, rgba(255,146,0,0.25) 0%, transparent 70%);
    pointer-events: none;
  }

  .profile-card > * { position: relative; z-index: 1; }

  /* hex avatar clip */
  .hex-avatar {
    clip-path: polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);
  }

  /* neon glow ring around avatar */
  .avatar-ring {
    background: linear-gradient(135deg, #FF9200, #ffcc00, #FF9200);
    padding: 3px;
    clip-path: polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);
  }

  .avatar-inner {
    clip-path: polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  /* stat chip */
  .stat-chip {
    background: rgba(255,146,0,0.08);
    border: 1px solid rgba(255,146,0,0.35);
    backdrop-filter: blur(4px);
    transition: all 0.2s ease;
  }
  .stat-chip:hover {
    background: rgba(255,146,0,0.18);
    border-color: rgba(255,146,0,0.7);
    box-shadow: 0 0 8px rgba(255,146,0,0.4);
  }

  /* glowing label */
  .gamer-name {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-shadow: 0 0 12px rgba(255,146,0,0.6);
  }

  /* thin orange divider with glow */
  .neon-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, #FF9200, transparent);
    box-shadow: 0 0 6px rgba(255,146,0,0.5);
    border: none;
    margin: 10px 0;
  }

  /* contact row */
  .contact-row {
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    color: rgba(255,255,255,0.45);
  }
`;

export default function SidebarProfile() {
  const { userData } = useAuth();
  if (!userData) return null;

  const {
    user_name,
    user_mobile,
    user_email,
    profession,
    bio,
    vigor_points = 0,
    vigor_coins = 0,
  } = userData;

  const safeVigorPoints = Number.isFinite(Number(vigor_points))
    ? Number(vigor_points)
    : 0;
  const safeVigorCoins = Number.isFinite(Number(vigor_coins))
    ? Number(vigor_coins)
    : 0;

  return (
    <>
      <style>{css}</style>

      <div className="profile-card px-4 pt-5 pb-4">

        {/* ── Avatar + Name block ── */}
        <div className="flex items-center gap-4">

          {/* hexagonal avatar */}
          <div className="avatar-ring flex-shrink-0" style={{ width: 58, height: 66 }}>
            <div className="avatar-inner bg-[#0d0120]" style={{ width: '100%', height: '100%' }}>
              <ClientProfilePhoto className="w-full h-full object-cover block" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {/* rank tag */}
              <span
                className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-sm"
                style={{
                  background: 'rgba(255,146,0,0.15)',
                  border: '1px solid rgba(255,146,0,0.5)',
                  color: '#FF9200',
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                PLAYER
              </span>
            </div>
            <h5 className="gamer-name text-white text-xl leading-tight truncate">{user_name}</h5>
            {profession && (
              <p
                className="text-xs truncate mt-0.5"
                style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Exo 2', sans-serif" }}
              >
                {profession}
              </p>
            )}
          </div>
        </div>

        {/* bio */}
        {bio && (
          <p
            className="mt-3 text-xs line-clamp-2 leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.45)', fontFamily: "'Exo 2', sans-serif" }}
          >
            {bio}
          </p>
        )}

        <div className="neon-divider" />

        {/* ── Stats row ── */}
        <div className="flex gap-2">

          {/* Points */}
          <div className="stat-chip flex-1 flex items-center gap-2 rounded-md px-3 py-2">
            <StarIcon size={15} style={{ color: '#FF9200' }} />
            <div>
              <div
                className="text-xs leading-none"
                style={{ color: 'rgba(255,255,255,0.45)', fontFamily: "'Exo 2', sans-serif" }}
              >
                Points
              </div>
              <div
                className="font-bold text-sm leading-snug text-white"
                style={{ fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.05em' }}
              >
                {safeVigorPoints.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Coins */}
          <div className="stat-chip flex-1 flex items-center gap-2 rounded-md px-3 py-2">
            {/* reuse the existing coin SVG */}
            <svg
              viewBox="69.8655 71.9463 339.481 339.4806"
              className="flex-shrink-0"
              style={{ width: 18, height: 18 }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <g transform="matrix(0.6630480289459229,0,0,0.6630480289459229,69.86547088623047,71.94631958007815)">
                <path fill="#EFB832" d="M256,512C114.841,512,0,397.159,0,256S114.841,0,256,0s256,114.841,256,256S397.159,512,256,512z"/>
                <g transform="matrix(1.190054,0,0,1.190054,-45.374573,225.235107)">
                  <path d="M 211.034 99.593 C 211.254 99.943 211.784 99.723 211.704 99.323 L 185.544 -30.727 C 178.904 -28.037 169.044 -16.087 166.294 -12.637 C 164.904 -10.887 164.044 -7.817 164.144 -4.467 C 164.184 -3.307 164.324 -2.137 164.574 -1.007 C 167.754 13.223 176.554 44.703 211.034 99.593 Z" fill="rgb(255,146,0)"/>
                  <path d="M 248.254 33.883 C 248.254 33.883 256.364 -21.147 259.854 -24.107 C 262.004 -25.927 309.584 -27.437 309.584 -27.437 L 309.154 25.973 C 309.644 27.573 309.064 29.243 307.814 30.353 C 243.624 87.493 221.044 102.233 214.994 110.713 C 214.634 111.223 215.144 111.963 215.714 111.703 C 267.524 88.273 320.544 53.003 339.274 38.663 C 341.604 36.883 341.314 33.983 341.344 31.053 C 341.824 -8.777 344.194 -47.257 338.934 -54.877 C 338.384 -55.677 337.554 -56.257 336.614 -56.497 C 295.304 -66.957 245.104 -61.197 233.844 -58.067 C 232.204 -57.607 230.954 -56.307 230.554 -54.657 C 216.344 3.093 223.664 70.123 224.734 81.373 C 224.784 81.903 225.414 82.143 225.814 81.793 L 290.464 25.773 C 292.164 24.253 292.284 21.623 290.734 19.953 L 267.434 -14.657 C 266.324 -15.777 264.844 -14.557 265.274 -13.037 C 270.104 4.153 273.374 16.023 273.374 16.023 L 248.254 33.883 Z" fill="rgb(18,2,57)"/>
                </g>
                <g className="opacity-50">
                  <path fill="#AE8132" d="M256.001,472.633c-119.452,0-216.633-97.181-216.633-216.633s97.18-216.633,216.633-216.633 c119.452,0,216.633,97.18,216.633,216.633S375.453,472.633,256.001,472.633z M256.001,94.604 c-88.994,0-161.396,72.402-161.396,161.396s72.402,161.396,161.396,161.396c88.994,0,161.396-72.402,161.396-161.396 S344.994,94.604,256.001,94.604z"/>
                </g>
                <g>
                  <circle fill="#EFB832" cx="65.963" cy="256" r="9.56"/>
                  <circle fill="#EFB832" cx="444.121" cy="256" r="9.56"/>
                </g>
              </g>
            </svg>
            <div>
              <div
                className="text-xs leading-none"
                style={{ color: 'rgba(255,255,255,0.45)', fontFamily: "'Exo 2', sans-serif" }}
              >
                Tokens
              </div>
              <div
                className="font-bold text-sm leading-snug text-white"
                style={{ fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.05em' }}
              >
                {safeVigorCoins.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* contact info */}
        <div className="contact-row mt-3 flex flex-col gap-0.5">
          {user_mobile && <span>{user_mobile}</span>}
          {user_email && <span className="truncate">{user_email}</span>}
        </div>
      </div>
    </>
  );
}